const EVENT_HISTORY_PATH = "/eventHistory";
const EVENT_HISTORY_DETAIL_PATH = `${EVENT_HISTORY_PATH}/eventReportDetail/`;
const SAFE_EVENT_HISTORY_PARAMETER_KEYS = new Set([
  "yard",
  "location",
  "eventType",
  "fromDate",
  "toDate",
  "userId",
  "systemName",
  "licensePlateNumber",
  "vehicleOwner",
  "vehicleType",
  "vehicleNumber",
  "seal",
  "annotation",
  "visitReason",
  "loadIdentifierType",
  "loadIdentifier",
  "visitId",
  "locationPlanId",
  "page",
  "rowsPerPage",
  "eventId",
  "gateOperationId",
  "timelineCenter",
  "timelineDoor",
]);
const SAFE_INTERACTION_TYPES = new Set([
  "button",
  "submit",
  "reset",
  "text",
  "search",
  "checkbox",
  "radio",
  "datetime-local",
]);
const SAFE_INTERACTION_ROLES = new Set(["button", "link", "menuitem", "dialog"]);

export const isEventHistoryPath = (pathname?: string): boolean =>
  Boolean(pathname?.includes(EVENT_HISTORY_PATH));

export const sanitizeEventHistoryPath = (pathname?: string): string | undefined => {
  if (!pathname || !isEventHistoryPath(pathname)) return pathname;
  const detailIndex = pathname.indexOf(EVENT_HISTORY_DETAIL_PATH);
  if (detailIndex < 0) return pathname;
  return `${pathname.slice(0, detailIndex)}${EVENT_HISTORY_DETAIL_PATH}:eventId`;
};

export const sanitizeEventHistoryUrl = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    if (!isEventHistoryPath(url.pathname)) return value;
    url.pathname = sanitizeEventHistoryPath(url.pathname) ?? url.pathname;
    url.search = url.search ? "?redacted" : "";
    url.hash = url.hash ? "#redacted" : "";
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : url.toString();
  } catch {
    return isEventHistoryPath(value) ? `${EVENT_HISTORY_PATH}?redacted` : value;
  }
};

const redactIfPresent = (value: unknown): unknown =>
  typeof value === "string" && value ? "[redacted]" : value;

const getChangedParameterKeys = (value: unknown): Record<string, string[]> | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const changes = value as Record<string, unknown>;
  const keysFor = (changeType: string): string[] => {
    const changed = changes[changeType];
    return changed && typeof changed === "object"
      ? Object.keys(changed).filter((key) => SAFE_EVENT_HISTORY_PARAMETER_KEYS.has(key))
      : [];
  };
  return {
    addedKeys: keysFor("added"),
    modifiedKeys: keysFor("modified"),
    removedKeys: keysFor("removed"),
  };
};

export const sanitizeEventHistoryRouteData = (
  data: Record<string, unknown>
): Record<string, unknown> => ({
  currentPath:
    typeof data.currentPath === "string"
      ? sanitizeEventHistoryPath(data.currentPath)
      : data.currentPath,
  previousPath:
    typeof data.previousPath === "string"
      ? sanitizeEventHistoryPath(data.previousPath)
      : data.previousPath,
  timeOnPreviousPage: data.timeOnPreviousPage,
  timestamp: data.timestamp,
  referrer:
    typeof data.referrer === "string" ? sanitizeEventHistoryUrl(data.referrer) : data.referrer,
  searchParams: redactIfPresent(data.searchParams),
  hash: redactIfPresent(data.hash),
  navigationTiming: data.navigationTiming,
  fullUrl: typeof data.fullUrl === "string" ? sanitizeEventHistoryUrl(data.fullUrl) : data.fullUrl,
  searchParamsChanged: data.searchParamsChanged,
  hashChanged: data.hashChanged,
  previousSearch: redactIfPresent(data.previousSearch),
  currentSearch: redactIfPresent(data.currentSearch),
  previousHash: redactIfPresent(data.previousHash),
  currentHash: redactIfPresent(data.currentHash),
  urlParamChanges: getChangedParameterKeys(data.urlParamChanges),
});

export const getEventHistoryInteractionId = (target: HTMLElement): string => {
  const interactiveTarget =
    target.closest<HTMLElement>("button, a, input, select, textarea, [role]") ?? target;
  const tagName = interactiveTarget.tagName.toLowerCase();
  const rawType = interactiveTarget.getAttribute("type");
  const rawRole = interactiveTarget.getAttribute("role");
  const type = rawType && SAFE_INTERACTION_TYPES.has(rawType) ? rawType : "";
  const role = rawRole && SAFE_INTERACTION_ROLES.has(rawRole) ? rawRole : "";
  const attributes = [type ? `type="${type}"` : "", role ? `role="${role}"` : ""].filter(Boolean);
  const attributeText = attributes.length > 0 ? ` ${attributes.join(" ")}` : "";
  return `<${tagName}${attributeText}>event-history</${tagName}>`;
};
